import { createReducer, on } from '@ngrx/store';
import {
  ChatChannelUpdatedData,
  ChatChannelTypeEnum,
  ChatConversation,
  ChatMessage,
  ChatSavedMessage,
  ChatUnreadUpdatedData,
  ChatUserSetting,
} from 'lib-trend-core';
import * as ChatActions from './chat.actions';
import { ChatBootstrapErrorKind, ChatState, ChatUnreadEntry, initialChatState } from './chat.state';

// Internal Chat feature reducer (SPEC-2026-07-internal-chat, T-18). Keeps the
// sidebar dataset live: bootstrap snapshot + socket deltas (unread/preview/
// presence/status/channel lifecycle) + optimistic command reflections.

/** Immutable single-conversation patch helper. */
function patchConversation(
  conversations: ChatConversation[],
  channelId: string,
  patch: (conversation: ChatConversation) => ChatConversation,
): ChatConversation[] {
  const index = conversations.findIndex((conversation) => conversation._id === channelId);
  if (index === -1) return conversations;
  const updated = [...conversations];
  updated[index] = patch(updated[index]);
  return updated;
}

/** Bootstrap `unreadCounts` (id → count) enriched with membership manualUnread. */
function buildUnreadEntries(
  counts: Record<string, number>,
  conversations: ChatConversation[],
): Record<string, ChatUnreadEntry> {
  const entries: Record<string, ChatUnreadEntry> = {};
  Object.keys(counts || {}).forEach((channelId) => {
    const count = counts[channelId] ?? 0;
    if (count <= 0) return;
    const conversation = conversations.find((item) => item._id === channelId);
    entries[channelId] = {
      count,
      mentioned: false,
      manualUnread: conversation?.membership?.manualUnread ?? false,
    };
  });
  return entries;
}

/**
 * Bookmarked message ids of a saved page (T-20). `GET /api/chat/saved` returns
 * `message` POPULATED, but the field is typed as `ChatMessage | string` at rest
 * — both shapes are accepted so a lean response never breaks the toolbar state.
 */
function extractSavedIds(saved: ChatSavedMessage[]): string[] {
  return (saved ?? [])
    .map((entry) => (typeof entry.message === 'string' ? entry.message : (entry.message as ChatMessage)?._id))
    .filter((messageId): messageId is string => !!messageId);
}

/**
 * Bootstrap failure → degradation kind (T-25). A transport failure (status 0 /
 * absent), any 5xx and a missing endpoint (404 — API deployed without the chat
 * module) all mean "the chat backend is unreachable"; everything else reached
 * the backend and is a plain, retryable error.
 */
function classifyBootstrapError(status?: number): ChatBootstrapErrorKind {
  if (!status || status >= 500 || status === 404) return 'unavailable';
  return 'error';
}

function applyUnread(state: ChatState, update: ChatUnreadUpdatedData): ChatState {
  if (!update?.channelId) return state;

  const unreadCounts = { ...state.unreadCounts };
  if (update.count > 0) {
    const previous = unreadCounts[update.channelId];
    unreadCounts[update.channelId] = {
      count: update.count,
      mentioned: update.mentioned ?? previous?.mentioned ?? false,
      manualUnread: update.manualUnread ?? previous?.manualUnread ?? false,
    };
  } else {
    delete unreadCounts[update.channelId];
  }

  let conversations = state.conversations;
  // Fan-out shape carries the preview — keep row preview/activity honest.
  if (update.preview !== undefined) {
    conversations = patchConversation(conversations, update.channelId, (conversation) => ({
      ...conversation,
      lastMessagePreview: update.preview,
      lastMessageAt: new Date(),
    }));
  }
  // READ / MARK_UNREAD shape carries the authoritative manualUnread flag.
  if (update.manualUnread !== undefined) {
    conversations = patchConversation(conversations, update.channelId, (conversation) => ({
      ...conversation,
      membership: conversation.membership
        ? { ...conversation.membership, manualUnread: update.manualUnread }
        : conversation.membership,
    }));
  }

  return { ...state, unreadCounts, conversations };
}

function applyChannel(state: ChatState, update: ChatChannelUpdatedData, currentUserId: string): ChatState {
  if (!update?.channelId) return state;

  switch (update.action) {
    // A channel/group just created (ChannelService fan-out) is seeded into the
    // sidebar dataset right away from the fields the event carries — name for
    // PUBLIC/PRIVATE, the member roster for GROUP (what the header/list labels
    // derive the display name from). Without the seed, the auto-opened pane
    // renders NAMELESS until the bootstrap roundtrip returns. The stub is
    // replaced wholesale by that refresh (refreshOnChannelUpdateEffect$), which
    // brings the authoritative row (membership, company, timestamps). DIRECT
    // never lands here — DMs materialize on the first send, with no `created`
    // fan-out.
    case 'created': {
      if (!update.type || state.conversations.some((conversation) => conversation._id === update.channelId)) {
        return state;
      }
      const stub = {
        _id: update.channelId,
        type: update.type as ChatChannelTypeEnum,
        name: update.name,
        company: '',
        createdBy: update.actorId ?? '',
        archived: false,
        pinnedMessages: [],
        members: update.memberIds,
        membership: null,
        // Activity-sort seed: a conversation created this instant belongs at
        // the top of the list until its first message stamps lastMessageAt.
        createdAt: new Date(),
      } as unknown as ChatConversation;
      return { ...state, conversations: [...state.conversations, stub] };
    }

    case 'archived':
      return {
        ...state,
        conversations: patchConversation(state.conversations, update.channelId, (conversation) => ({
          ...conversation,
          archived: true,
          archivedBy: update.actorId,
          archivedAt: new Date(),
        })),
      };

    case 'left': {
      if (update.actorId === currentUserId) {
        const unreadCounts = { ...state.unreadCounts };
        delete unreadCounts[update.channelId];
        return {
          ...state,
          conversations: state.conversations.filter((conversation) => conversation._id !== update.channelId),
          unreadCounts,
          activeChannelId: state.activeChannelId === update.channelId ? null : state.activeChannelId,
        };
      }
      return {
        ...state,
        conversations: patchConversation(state.conversations, update.channelId, (conversation) => ({
          ...conversation,
          members: (conversation.members ?? []).filter((memberId) => memberId !== update.actorId),
        })),
      };
    }

    // Unlike 'left' (which only removes the actor), a deleted channel is gone
    // for EVERY member: drop the row, its unread entry and, when it was on
    // screen, the active selection — the workspace falls back to its empty
    // state and the bubble popup returns to the list (chat-bubble-host).
    case 'deleted': {
      const unreadCounts = { ...state.unreadCounts };
      delete unreadCounts[update.channelId];
      return {
        ...state,
        conversations: state.conversations.filter((conversation) => conversation._id !== update.channelId),
        unreadCounts,
        activeChannelId: state.activeChannelId === update.channelId ? null : state.activeChannelId,
      };
    }

    case 'joined':
    case 'invited': {
      // Roster refresh for known channels; unknown/created channels are picked
      // up by the bootstrap refresh effect.
      const memberIds = update.memberIds ?? (update.actorId ? [update.actorId] : []);
      return {
        ...state,
        conversations: patchConversation(state.conversations, update.channelId, (conversation) => ({
          ...conversation,
          members: Array.from(new Set([...(conversation.members ?? []), ...memberIds])),
        })),
      };
    }

    default:
      return state;
  }
}

export const chatReducers = createReducer(
  initialChatState,

  // bootstrap ---------------------------------------------------------------
  on(ChatActions.loadBootstrap, (state) => ({ ...state, isLoading: true, loadError: false })),
  on(ChatActions.setBootstrap, (state, action) => {
    const conversations = action.data?.conversations ?? [];
    // Promote a pending DM draft once its channel materialized (RF-01).
    const draftMatch = state.directDraft
      ? conversations.find((conversation) => conversation.directKey === state.directDraft.directKey)
      : undefined;
    return {
      ...state,
      isLoading: false,
      loadError: false,
      bootstrapLoaded: true,
      conversations,
      favoriteSections: action.data?.favoriteSections ?? [],
      unreadCounts: buildUnreadEntries(action.data?.unreadCounts ?? {}, conversations),
      presence: (action.data?.presence?.onlineUserIds ?? [])
        .reduce((online, userId) => ({ ...online, [userId]: true }), {} as Record<string, boolean>),
      statuses: (action.data?.userStatuses ?? []).reduce((statuses, status) => ({
        ...statuses,
        [status.user]: {
          availability: status.availability,
          statusEmoji: status.statusEmoji,
          statusText: status.statusText,
          statusExpiresAt: status.statusExpiresAt ? String(status.statusExpiresAt) : null,
        },
      }), {} as ChatState['statuses']),
      topEmojis: action.data?.topEmojis ?? [],
      mySettings: action.data?.mySettings ?? null,
      userDisplay: action.data?.userDisplay ?? {},
      activeChannelId: draftMatch ? draftMatch._id : state.activeChannelId,
      directDraft: draftMatch ? null : state.directDraft,
      bootstrapErrorKind: null,
      error: null,
    };
  }),
  on(ChatActions.setBootstrapFailure, (state, action) => ({
    ...state,
    isLoading: false,
    loadError: true,
    bootstrapErrorKind: classifyBootstrapError(action.status),
    error: action.error,
  })),

  // discover / saved ----------------------------------------------------------
  on(ChatActions.discoverChannels, (state) => ({ ...state, discoverLoading: true })),
  on(ChatActions.setDiscoveredChannels, (state, action) => ({
    ...state,
    discoverLoading: false,
    // "Load more" appends the next page; a fresh search replaces the list.
    discovered: action.append
      ? [
        ...state.discovered,
        ...action.channels.filter((channel) => !state.discovered.some((known) => known._id === channel._id)),
      ]
      : action.channels,
    discoverMeta: action.meta ?? null,
  })),
  on(ChatActions.applyDiscoveredJoin, (state, action) => ({
    ...state,
    discovered: state.discovered.map((channel) => (channel._id === action.channelId && !channel.isMember
      ? { ...channel, isMember: true, memberCount: (channel.memberCount ?? 0) + 1 }
      : channel)),
  })),
  on(ChatActions.loadSaved, (state) => ({ ...state, savedLoading: true })),
  on(ChatActions.setSaved, (state, action) => ({
    ...state,
    savedLoading: false,
    saved: action.saved,
    savedIds: extractSavedIds(action.saved),
    // Saved authors may not be in the bootstrap map (an old message from
    // someone no longer in a visible conversation) — merge, never replace.
    userDisplay: action.userDisplay
      ? { ...state.userDisplay, ...action.userDisplay }
      : state.userDisplay,
  })),

  // open conversation ---------------------------------------------------------
  on(ChatActions.openConversation, (state, action) => ({ ...state, activeChannelId: action.channelId, directDraft: null })),
  on(ChatActions.openDirectDraft, (state, action) => ({
    ...state,
    activeChannelId: null,
    directDraft: { userId: action.userId, directKey: action.directKey },
  })),
  on(ChatActions.closeConversation, (state) => ({ ...state, activeChannelId: null, directDraft: null })),

  // socket bridge -------------------------------------------------------------
  on(ChatActions.applyUnreadUpdate, (state, action) => applyUnread(state, action.update)),
  on(ChatActions.applyChannelUpdate, (state, action) => applyChannel(state, action.update, action.currentUserId)),
  on(ChatActions.applyPresenceUpdate, (state, action) => ({
    ...state,
    presence: { ...state.presence, [action.update.userId]: action.update.online },
  })),
  // RF-33/AC-29 — the counterpart read the DM. My own echo is ignored, and the
  // watermark only ever moves forward so an out-of-order event cannot un-read
  // messages that already showed as read.
  on(ChatActions.applyReadUpdate, (state, action) => {
    const { update, currentUserId } = action;
    if (!update?.channelId || !update.lastReadAt || update.userId === currentUserId) return state;

    const incoming = new Date(update.lastReadAt);
    if (Number.isNaN(incoming.getTime())) return state;

    return {
      ...state,
      conversations: patchConversation(state.conversations, update.channelId, (conversation) => {
        const current = conversation.counterpartLastReadAt
          ? new Date(conversation.counterpartLastReadAt)
          : null;
        if (current && current >= incoming) return conversation;
        return { ...conversation, counterpartLastReadAt: incoming };
      }),
    };
  }),
  on(ChatActions.applyStatusUpdate, (state, action) => ({
    ...state,
    statuses: {
      ...state.statuses,
      [action.update.userId]: {
        availability: action.update.availability,
        statusEmoji: action.update.statusEmoji,
        statusText: action.update.statusText,
        statusExpiresAt: action.update.statusExpiresAt,
      },
    },
  })),
  on(ChatActions.applyPinUpdate, (state, action) => ({
    ...state,
    conversations: patchConversation(state.conversations, action.update.channelId, (conversation) => ({
      ...conversation,
      pinnedMessages: action.update.action === 'pinned'
        ? [
          ...(conversation.pinnedMessages ?? []).filter((pin) => pin.message !== action.update.messageId),
          { message: action.update.messageId, pinnedBy: action.update.actorId, pinnedAt: new Date() },
        ]
        : (conversation.pinnedMessages ?? []).filter((pin) => pin.message !== action.update.messageId),
    })),
  })),

  // optimistic local updates --------------------------------------------------
  on(ChatActions.setConversationPreview, (state, action) => ({
    ...state,
    conversations: patchConversation(state.conversations, action.channelId, (conversation) => ({
      ...conversation,
      lastMessagePreview: action.preview,
      lastMessageAt: new Date(),
    })),
  })),
  on(ChatActions.setNotificationLevel, (state, action) => ({
    ...state,
    conversations: patchConversation(state.conversations, action.channelId, (conversation) => ({
      ...conversation,
      membership: conversation.membership
        ? { ...conversation.membership, notificationLevel: action.level }
        : conversation.membership,
    })),
  })),
  on(ChatActions.setManualUnread, (state, action) => ({
    ...state,
    unreadCounts: {
      ...state.unreadCounts,
      [action.channelId]: {
        count: Math.max(1, state.unreadCounts[action.channelId]?.count ?? 0),
        mentioned: state.unreadCounts[action.channelId]?.mentioned ?? false,
        manualUnread: true,
      },
    },
    conversations: patchConversation(state.conversations, action.channelId, (conversation) => ({
      ...conversation,
      membership: conversation.membership
        ? { ...conversation.membership, manualUnread: true }
        : conversation.membership,
    })),
  })),
  on(ChatActions.clearUnread, (state, action) => {
    const unreadCounts = { ...state.unreadCounts };
    delete unreadCounts[action.channelId];
    return {
      ...state,
      unreadCounts,
      conversations: patchConversation(state.conversations, action.channelId, (conversation) => ({
        ...conversation,
        membership: conversation.membership
          ? { ...conversation.membership, manualUnread: false }
          : conversation.membership,
      })),
    };
  }),

  // message actions (T-20) ------------------------------------------------------
  on(ChatActions.applySavedToggle, (state, action) => {
    const isSaved = state.savedIds.includes(action.messageId);
    if (isSaved === action.saved) return state;
    return {
      ...state,
      savedIds: action.saved
        ? [...state.savedIds, action.messageId]
        : state.savedIds.filter((messageId) => messageId !== action.messageId),
      // Unsaving also drops the row from an already-loaded saved list so the
      // view reacts without waiting for the refresh.
      saved: action.saved
        ? state.saved
        : state.saved.filter((entry) => (typeof entry.message === 'string'
          ? entry.message !== action.messageId
          : (entry.message as ChatMessage)?._id !== action.messageId)),
    };
  }),
  on(ChatActions.setThreadSeen, (state, action) => {
    // Monotonic: a stale (smaller) count must never resurrect the unseen dot.
    if ((state.threadSeen[action.messageId] ?? 0) >= action.count) return state;
    return { ...state, threadSeen: { ...state.threadSeen, [action.messageId]: action.count } };
  }),
  on(ChatActions.mergeUserDisplay, (state, action) => ({
    ...state,
    userDisplay: { ...state.userDisplay, ...(action.userDisplay ?? {}) },
  })),

  // cross-surface broadcasts (SPEC-2026-09-internal-chat-pwa, RF-23) --------------
  on(ChatActions.setFavoriteSections, (state, action) => ({
    ...state,
    favoriteSections: action.favoriteSections ?? [],
  })),

  // status & preferences (T-21) --------------------------------------------------
  on(ChatActions.patchMySettings, (state, action) => ({
    ...state,
    // A null snapshot (bootstrap answered before the settings doc existed) is
    // patched over an empty object so the optimistic value still renders.
    mySettings: { ...((state.mySettings ?? {}) as ChatUserSetting), ...(action.patch ?? {}) },
  })),

  // composer drafts (T-19) ------------------------------------------------------
  on(ChatActions.setComposerDraft, (state, action) => ({
    ...state,
    composerDrafts: { ...state.composerDrafts, [action.key]: action.draft },
  })),
  on(ChatActions.clearComposerDraft, (state, action) => {
    if (!(action.key in state.composerDrafts)) return state;
    const composerDrafts = { ...state.composerDrafts };
    delete composerDrafts[action.key];
    return { ...state, composerDrafts };
  }),

  // interface states (T-25) -----------------------------------------------------
  on(ChatActions.setConnectionStatus, (state, action) => {
    if (state.connectionStatus === action.status) return state;
    // Coming back from a visible outage is the only case that shows the brief
    // "Conectado" bar — the first connect of the session must stay silent.
    const recovered = action.status === 'online'
      && (state.connectionStatus === 'reconnecting' || state.connectionStatus === 'offline');
    return {
      ...state,
      connectionStatus: action.status,
      connectionRestored: recovered ? true : (action.status === 'online' && state.connectionRestored),
    };
  }),
  on(ChatActions.clearConnectionRestored, (state) => (
    state.connectionRestored ? { ...state, connectionRestored: false } : state
  )),

  // failure -------------------------------------------------------------------
  on(ChatActions.setFailure, (state, action) => ({
    ...state,
    discoverLoading: false,
    savedLoading: false,
    error: action.error,
  })),
);
